import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from './GlassCard';

const { FiMenu, FiX, FiActivity } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Main navigation items (header)
  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Upload', path: '/upload' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-50 p-2 sm:p-4">
      <GlassCard className="max-w-7xl mx-auto">
        <div className="px-3 py-3 sm:px-6 sm:py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
              <div className="relative">
                <SafeIcon 
                  icon={FiActivity} 
                  className="h-6 w-6 sm:h-8 sm:w-8 text-green-400 group-hover:text-green-300 transition-colors duration-300" 
                />
                <div className="absolute -inset-2 bg-green-400/30 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent terminal-text">
                  MedExplainGPT
                </span>
                <span className="text-xs text-green-300 -mt-1">Australia 🇦🇺</span>
              </div>
            </Link>

            {/* Desktop Navigation - Simplified */}
            <div className="hidden md:flex items-center space-x-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 relative group ${
                    isActive(item.path)
                      ? 'text-green-300 bg-green-400/20 ring-1 ring-green-400/40'
                      : 'text-green-200 hover:text-green-300 hover:bg-black/30'
                  }`}
                >
                  {item.name}
                  {isActive(item.path) && (
                    <motion.div
                      className="absolute bottom-0 left-1/2 w-1 h-1 bg-green-400 rounded-full"
                      layoutId="activeTab"
                      style={{ x: '-50%' }}
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-lg text-green-300 hover:text-green-200 hover:bg-black/30 transition-all duration-300"
              >
                <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation - Simplified */}
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-4 pt-4 border-t border-green-500/20"
            >
              <div className="flex flex-col space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                      isActive(item.path)
                        ? 'text-green-300 bg-green-400/20 ring-1 ring-green-400/40'
                        : 'text-green-200 hover:text-green-300 hover:bg-black/30'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Navigation scan line */}
        <motion.div
          className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-green-400/60 to-transparent"
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 4, repeat: Infinity, repeatDelay: 3, ease: 'linear' }}
        />
      </GlassCard>
    </nav>
  );
};

export default Navbar;